/**
 * repositories/pendingRegistrationsRepository.ts
 * Pending registrations table data access layer
 *
 * Provides type-safe data access for pending_registrations table with centralized mapping
 * between database snake_case and application camelCase.
 *
 * Project: P07 - Subscription Flow
 * Architecture: Repository Pattern per CODING_STANDARDS.md
 */

import { supabase } from '@/lib/supabase';
import { toDomain } from '@/lib/supabaseMappers';
import type {
  PendingRegistration,
  PlanSelection,
  RegistrationValidationResult,
} from '@/types/supabase/PendingRegistration';

/**
 * Pending Registrations Repository
 *
 * Provides data access for pending registration records.
 * These records are created during registration and deleted
 * after successful payment completion.
 */
export const pendingRegistrationsRepository = {
  /**
   * Get pending registration by user ID
   *
   * @param userId - User ID (UUID)
   * @returns Promise<PendingRegistration | null> - Registration if found, null otherwise
   */
  async getByUserId(userId: string): Promise<PendingRegistration | null> {
    const { data, error } = await supabase
      .from('pending_registrations')
      .select('*')
      .eq('user_id', userId)
      .maybeSingle();

    if (error) throw error;
    if (!data) return null;

    return toDomain<PendingRegistration>(data);
  },

  /**
   * Get plan selection for payment page (subset of fields)
   *
   * Optimized query that only fetches plan-related fields.
   *
   * @param userId - User ID (UUID)
   * @returns Promise<PlanSelection | null> - Plan selection if found, null otherwise
   */
  async getPlanSelectionByUserId(userId: string): Promise<PlanSelection | null> {
    const { data, error } = await supabase
      .from('pending_registrations')
      .select('plan_type, billing_interval')
      .eq('user_id', userId)
      .maybeSingle();

    if (error) throw error;
    if (!data) return null;

    return toDomain<PlanSelection>(data);
  },

  /**
   * Check if user has a pending registration
   *
   * @param userId - User ID (UUID)
   * @returns Promise<boolean> - True if user has pending registration
   */
  async existsByUserId(userId: string): Promise<boolean> {
    const { data, error } = await supabase
      .from('pending_registrations')
      .select('id')
      .eq('user_id', userId)
      .maybeSingle();

    if (error) throw error;
    return data !== null;
  },

  /**
   * Validate a pending registration token
   *
   * Calls the database RPC function to check if token is valid,
   * expired, or already activated.
   *
   * @param token - Verification token from email link
   * @returns Promise with validation result in domain format (camelCase)
   */
  async validateToken(token: string): Promise<RegistrationValidationResult[]> {
    const { data, error } = await supabase.rpc('validate_pending_registration', {
      p_token: token,
    });

    if (error) throw error;

    // RPC returns an array, map each result to domain type
    if (!data || !Array.isArray(data)) {
      return [];
    }

    return data.map((row: unknown) => toDomain<RegistrationValidationResult>(row));
  }
};
